import './index.scss';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import { RootState } from '@/src/reducers/types';

import { useGetFolders } from './api/getFolders';
import Container from './components/Container/Container';
import Header from './components/Header/Header';
import List from './components/List/List';
import { useUndoMoveToast } from './components/List/useUndoMoveToast';
import Sidebar from './components/Sidebar/Sidebar';
import { FileEntryViewerPage } from './FileEntryViewerPage';
import { LibrarySearchParamsProvider } from './store/filters.store';
import { LibraryListContextProvider, useLibraryListStore } from './store/library-list.store';

import Spinner from '../../common/Elements/Spinner/Spinner';
import { useGetProjectPermissions } from '../api/getProjectPermissions';

const findFolderPath = (folders: any[], id: number, path: any[] = []) => {
  for (const folder of folders) {
    const newPath = [...path, folder];

    if (folder.id === id) return newPath;
    if (folder.children?.length) {
      const foundPath = findFolderPath(folder.children, id, newPath);
      if (foundPath) return foundPath;
    }
  }
  return null;
};

export const LibraryComponent = () => {
  const dispatch = useDispatch();
  const { projectId } = useParams<{ projectId: string }>();
  const permissions = useLibraryListStore((s) => s.permissions);
  const files = useSelector((state: RootState) => state.files);
  const foldersQuery = useGetFolders({
    projectId: +projectId,
    config: {
      enabled: permissions.library.access,
    },
  });
  const folders = foldersQuery.data?.data;
  const undoMovetoast = useUndoMoveToast();
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const location = useLocation();

  const { fileId: qsFileId, sourceId: qsSourceId } = React.useMemo(() => {
    const queryIndex = location.search.indexOf('?');
    const queryString = queryIndex !== -1 ? location.search.substring(queryIndex + 1) : '';

    const params = new URLSearchParams(queryString);

    return {
      action: params.get('action'),
      fileId: params.get('file-id'),
      sourceId: params.get('source-id'),
    };
  }, [location.search]);

  useEffect(() => {
    dispatch({ type: 'RESET_FILES_SIDEBAR_ACTIVE' });
    dispatch({ type: 'UPDATE_FILES_ACTIVE_FOLDER', folder: 'null' });
    dispatch({ type: 'UPDATE_FILES_SIDEBAR_ACTIVE', menu: { id: 'library', label: 'Library' } });

    return () => {
      dispatch({ type: 'UPDATE_FILES_ACTIVE_FOLDER', folder: 'null' });
    };
  }, []);

  useEffect(() => {
    if (files.activeFolder?.id) {
      if (!folders) {
        return;
      }

      const path = findFolderPath(folders, files.activeFolder.id);
      if (path) {
        setBreadcrumbs(path);
      }
    } else {
      setBreadcrumbs([]);
    }
  }, [folders, files.activeFolder.id]);

  useEffect(() => {
    if (files.showDeleteModal) {
      $('#deleteFile').modal('show');
    }
  }, [files.showDeleteModal]);

  if (qsFileId) {
    return <FileEntryViewerPage libraryItemId={+qsFileId} sourceId={+qsSourceId} />;
  }
  if (permissions.library.access && foldersQuery.isFetched && foldersQuery.data === undefined) {
    return <Spinner />;
  }

  return (
    <div className="filesSection">
      <Header projectId={projectId} breadcrumbs={breadcrumbs} />
      <Container
        render={(dragEvent) => (
          <>
            <Sidebar
              draggingData={dragEvent.draggingData}
              draggingFromFiles={dragEvent.draggingFromFiles}
              folders={folders}
            />

            <List projectId={projectId} draggingData={dragEvent.draggingData} />
          </>
        )}
      />

      {undoMovetoast.toastElement}
    </div>
  );
};

export function withLibraryListContext<P>(Component: React.ComponentType<P>) {
  return function WrappedWithLibraryListContext(props: P) {
    const { projectId } = useParams<{ projectId: string }>();
    const libraryPermissionsQuery = useGetProjectPermissions({ projectId: +projectId });
    const permissions = libraryPermissionsQuery.data?.data;
    return (
      <React.Fragment>
        {libraryPermissionsQuery.isFetched && (
          <LibrarySearchParamsProvider>
            <LibraryListContextProvider permissions={permissions}>
              <Component {...props} />
            </LibraryListContextProvider>
          </LibrarySearchParamsProvider>
        )}
      </React.Fragment>
    );
  };
}

export const Library = withLibraryListContext(LibraryComponent);
